/* CloudDream storefront money presentation.
 *
 * Shopify's theme output is formatted in Liquid. Essential Cart Drawer owns
 * its injected markup, so this observer is deliberately scoped to that app's
 * portal instead of rewriting money text across the document.
 */
(function (scope) {
  'use strict';

  var PORTAL_ID = 'essential-cart-drawer-portal';
  var observedPortal = null;
  var portalObserver = null;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    function withDelimiters(number, precision, thousands, decimal) {
      var value = Number(number);
      var places = precision == null ? 2 : precision;
      var thousandsSeparator = thousands == null ? ',' : thousands;
      var decimalSeparator = decimal == null ? '.' : decimal;
      if (!Number.isFinite(value)) return '0';

      var parts = (value / 100).toFixed(places).split('.');
      var integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousandsSeparator);
      return integer + (parts[1] ? decimalSeparator + parts[1] : '');
    }

    var amount;
    switch (match[1]) {
      case 'amount':
        amount = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        amount = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        amount = withDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, amount);
  }

  function money(cents, format) {
    return formatMoney(cents, format).replace(/([.,])00(?!\d)/, '');
  }

  function normalizeCurrencyAmount(rawAmount) {
    var amountParts = rawAmount.replace(/,/g, '').split('.');
    var integer = Number.parseInt(amountParts[0], 10);
    var fraction = amountParts[1] || '';

    if (!Number.isFinite(integer)) return rawAmount;

    var formatted = integer.toLocaleString('en-US');
    if (fraction && !/^0+$/.test(fraction)) formatted += '.' + fraction;

    return formatted;
  }

  function normalizeCurrencyText(value) {
    if (typeof value !== 'string' || value.indexOf('Tk') === -1 && value.indexOf('৳') === -1) return value;

    return value.replace(/\bTk\s*/g, '৳').replace(/৳\s*([0-9][0-9,]*(?:\.[0-9]+)?)/g, function (_match, rawAmount) {
      return '৳' + normalizeCurrencyAmount(rawAmount);
    });
  }

  function formatCartItemCountLabel(value) {
    if (typeof value !== 'string') return '';

    var count = value.match(/[0-9][0-9,]*/);
    var words = value.trim().split(/\s+/);
    var noun = words[words.length - 1] || '';
    if (!count || !noun) return '';

    if (Number(count[0].replace(/,/g, '')) === 1 && /s$/i.test(noun)) noun = noun.slice(0, -1);

    return count[0] + ' ' + noun.charAt(0).toUpperCase() + noun.slice(1);
  }

  function syncCartItemCount(portal) {
    if (!portal) return;

    var list = portal.querySelector('[data-essential-cart-element="cart-drawer-item-list"]');
    var wrapper = portal.querySelector('[data-essential-cart-element="cart-drawer-item-list-wrapper"]');
    if (!list || !wrapper) return;

    var label = formatCartItemCountLabel(list.getAttribute('aria-label'));
    if (label && wrapper.getAttribute('data-cd-cart-item-count') !== label) {
      wrapper.setAttribute('data-cd-cart-item-count', label);
    }
  }

  function setCheckoutButtonLabel(portal, label) {
    if (!portal || typeof label !== 'string' || !label.trim()) return false;

    var button = portal.querySelector('[data-essential-cart-element="cart-drawer-checkout-button"]');
    if (!button) return false;

    if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    return true;
  }

  function syncCheckoutButtonLabel(portal) {
    var label = scope.cartStrings && scope.cartStrings.checkout;
    return setCheckoutButtonLabel(portal, label);
  }

  function followsCurrencyMarker(node) {
    var current = node;

    for (var depth = 0; current && depth < 4; depth += 1) {
      var sibling = current.previousSibling || current.previousElementSibling;
      while (sibling && !sibling.textContent.trim()) sibling = sibling.previousSibling;
      if (sibling && sibling.textContent.trim() === '৳') return true;
      current = current.parentElement;
    }

    return false;
  }

  function normalizeNode(node) {
    if (!node) return;

    if (node.nodeType === 3) {
      var parent = node.parentElement;
      if (parent && parent.matches('script, style, template')) return;

      var nextValue = normalizeCurrencyText(node.nodeValue);
      if (nextValue === node.nodeValue && followsCurrencyMarker(node)) {
        nextValue = node.nodeValue.replace(/^(\s*)([0-9][0-9,]*(?:\.[0-9]+)?)(\s*)$/, function (_match, before, amount, after) {
          return before + normalizeCurrencyAmount(amount) + after;
        });
      }
      if (nextValue !== node.nodeValue) node.nodeValue = nextValue;
      return;
    }

    if (node.nodeType !== 1) return;

    ['aria-label', 'title'].forEach(function (attribute) {
      if (!node.hasAttribute(attribute)) return;
      var currentValue = node.getAttribute(attribute);
      var nextValue = normalizeCurrencyText(currentValue);
      if (nextValue !== currentValue) node.setAttribute(attribute, nextValue);
    });

    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    var textNode;
    while ((textNode = walker.nextNode())) normalizeNode(textNode);

    node.querySelectorAll('[aria-label], [title]').forEach(function (element) {
      ['aria-label', 'title'].forEach(function (attribute) {
        if (!element.hasAttribute(attribute)) return;
        var currentValue = element.getAttribute(attribute);
        var nextValue = normalizeCurrencyText(currentValue);
        if (nextValue !== currentValue) element.setAttribute(attribute, nextValue);
      });
    });
  }

  function observePortal(portal) {
    if (!portal || portal === observedPortal) return;

    if (portalObserver) portalObserver.disconnect();
    observedPortal = portal;
    normalizeNode(portal);
    syncCartItemCount(portal);
    syncCheckoutButtonLabel(portal);

    portalObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'characterData') {
          normalizeNode(mutation.target);
          return;
        }

        if (mutation.type === 'attributes') {
          normalizeNode(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(normalizeNode);
      });
      syncCartItemCount(portal);
      syncCheckoutButtonLabel(portal);
    });

    portalObserver.observe(portal, {
      attributes: true,
      attributeFilter: ['aria-label', 'title'],
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  function findPortal() {
    observePortal(document.getElementById(PORTAL_ID));
  }

  function init() {
    findPortal();
    if (observedPortal) return;

    var mountObserver = new MutationObserver(function () {
      findPortal();
      if (observedPortal) mountObserver.disconnect();
    });

    mountObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      followsCurrencyMarker: followsCurrencyMarker,
      formatMoney: formatMoney,
      formatCartItemCountLabel: formatCartItemCountLabel,
      money: money,
      normalizeCurrencyAmount: normalizeCurrencyAmount,
      normalizeCurrencyText: normalizeCurrencyText,
      setCheckoutButtonLabel: setCheckoutButtonLabel,
    };
  }

  scope.CloudDreamMoney = {
    formatMoney: formatMoney,
    formatCartItemCountLabel: formatCartItemCountLabel,
    money: money,
    normalizeCurrencyAmount: normalizeCurrencyAmount,
    normalizeCurrencyText: normalizeCurrencyText,
    setCheckoutButtonLabel: setCheckoutButtonLabel,
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
    else init();
  }
})(typeof window !== 'undefined' ? window : globalThis);
